import { useState, useEffect } from "react";
import { seedAccommodations } from "@/mocks/accommodations";
import { getTopViewedPages } from "@/hooks/usePageView";
import { places } from "@/mocks/places";
import { restaurants } from "@/mocks/restaurants";

interface Review {
  id: string;
  restaurantId: string;
  rating: number;
  date: string;
  hidden?: boolean;
  helpful?: number;
}

interface PendingRestaurant {
  id: string;
  restaurantName: string;
  status: "pending" | "approved" | "rejected";
  appliedAt: string;
  city: string;
}

interface ManagedArticle {
  id: string;
  title: string;
  category: string;
  featured: boolean;
  createdAt: string;
}

interface ManagedPlace {
  id: string;
  name: string;
  city: string;
  createdAt: string;
}

interface AccommodationRecord {
  id: string;
  businessName: string;
  city: string;
  businessType: string;
  membershipTier?: "free" | "gold";
  status?: string;
  appliedAt?: string;
}

const MONTH_NAMES = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

const StatCard = ({
  icon,
  label,
  value,
  sub,
  color,
}: {
  icon: string;
  label: string;
  value: string | number;
  sub?: string;
  color: string;
}) => (
  <div className="bg-white rounded-xl border border-stone-100 p-5">
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${color}`}>
        <i className={`${icon} text-lg`}></i>
      </div>
    </div>
    <p className="text-2xl font-bold text-stone-900">{value}</p>
    <p className="text-xs font-semibold text-stone-600 mt-0.5">{label}</p>
    {sub && <p className="text-xs text-stone-400 mt-0.5">{sub}</p>}
  </div>
);

const SiteStats = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [applications, setApplications] = useState<PendingRestaurant[]>([]);
  const [adminArticles, setAdminArticles] = useState<ManagedArticle[]>([]);
  const [adminPlaces, setAdminPlaces] = useState<ManagedPlace[]>([]);
  const [activeTab, setActiveTab] = useState<"overview" | "reviews" | "content" | "accommodations" | "pageviews" | "analytics">("overview");
  const [allAccommodations, setAllAccommodations] = useState<AccommodationRecord[]>([]);
  const [topViewed, setTopViewed] = useState<ReturnType<typeof getTopViewedPages>>([]);
  const [announcementCount, setAnnouncementCount] = useState(0);
  const [gaMeasurementId, setGaMeasurementId] = useState(() => localStorage.getItem("gaMeasurementId") || "");
  const [gaIdInput, setGaIdInput] = useState(() => localStorage.getItem("gaMeasurementId") || "");
  const [gaIdSaved, setGaIdSaved] = useState(false);

  useEffect(() => {
    setReviews(JSON.parse(localStorage.getItem("restaurantReviews") || "[]"));
    setApplications(JSON.parse(localStorage.getItem("pendingRestaurants") || "[]"));
    setAdminArticles(JSON.parse(localStorage.getItem("adminArticles") || "[]"));
    setAdminPlaces(JSON.parse(localStorage.getItem("adminPlaces") || "[]"));

    // Load accommodations: seed + approved from localStorage
    const approved: AccommodationRecord[] = JSON.parse(localStorage.getItem("pendingAccommodations") || "[]")
      .filter((a: AccommodationRecord) => a.status === "approved");
    const memberships: Record<string, "free" | "gold"> = {};
    (JSON.parse(localStorage.getItem("accommodationMemberships") || "[]") as { id: string; tier: "free" | "gold" }[])
      .forEach((m) => { memberships[m.id] = m.tier; });
    const combined: AccommodationRecord[] = [
      ...seedAccommodations.map((a) => ({
        id: a.id,
        businessName: a.businessName,
        city: a.city,
        businessType: a.businessType,
        membershipTier: (memberships[a.id] || a.membershipTier || "free") as "free" | "gold",
        appliedAt: a.appliedAt,
      })),
      ...approved.map((a) => ({
        ...a,
        membershipTier: (memberships[a.id] || "free") as "free" | "gold",
      })),
    ];
    setAllAccommodations(combined);

    // Load page view data
    setTopViewed(getTopViewedPages("all", 20));

    // Load announcements count
    const storedAnn = JSON.parse(localStorage.getItem("siteAnnouncements") || "[]");
    const SEED_COUNT = 8; // SEED_ANNOUNCEMENTS length
    const allAnnIds = new Set([...storedAnn.map((a: { id: string }) => a.id)]);
    // seed ids: ann-1 to ann-8
    for (let i = 1; i <= SEED_COUNT; i++) allAnnIds.add(`ann-${i}`);
    setAnnouncementCount(allAnnIds.size);
  }, []);

  // Static mock counts for built-in content
  const BUILTIN_ARTICLES = 12;
  const BUILTIN_PLACES = 24;
  const BUILTIN_RESTAURANTS = 8;

  const totalArticles = BUILTIN_ARTICLES + adminArticles.length;
  const totalPlaces = BUILTIN_PLACES + adminPlaces.length;
  const approvedRestaurants = BUILTIN_RESTAURANTS + applications.filter((a) => a.status === "approved").length;
  const totalReviews = reviews.length;
  const visibleReviews = reviews.filter((r) => !r.hidden).length;
  const avgRating = reviews.length > 0
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : "—";

  // Rating distribution
  const ratingDist = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
  }));
  const maxRatingCount = Math.max(...ratingDist.map((r) => r.count), 1);

  // Monthly reviews (last 6 months)
  const now = new Date();
  const monthlyReviews = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    const label = MONTH_NAMES[d.getMonth()];
    const count = reviews.filter((r) => {
      const rd = new Date(r.date);
      return rd.getFullYear() === d.getFullYear() && rd.getMonth() === d.getMonth();
    }).length;
    return { label, count };
  });
  const maxMonthly = Math.max(...monthlyReviews.map((m) => m.count), 1);

  // Top restaurants by review count
  const restaurantReviewMap: Record<string, number> = {};
  reviews.forEach((r) => {
    restaurantReviewMap[r.restaurantId] = (restaurantReviewMap[r.restaurantId] || 0) + 1;
  });
  const topRestaurants = Object.entries(restaurantReviewMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  // Article categories
  const categoryMap: Record<string, number> = {};
  adminArticles.forEach((a) => {
    categoryMap[a.category] = (categoryMap[a.category] || 0) + 1;
  });
  const categoryList = Object.entries(categoryMap).sort((a, b) => b[1] - a[1]);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Site İstatistikleri
          </h2>
          <p className="text-stone-400 text-xs mt-0.5">İçerik ve yorum verilerine genel bakış</p>
        </div>
        <div className="flex items-center bg-stone-100 rounded-full p-1 gap-1 flex-wrap">
          {([
            { key: "overview", label: "Genel Bakış" },
            { key: "reviews", label: "Yorumlar" },
            { key: "content", label: "İçerik" },
            { key: "accommodations", label: "Konaklama" },
            { key: "pageviews", label: "Sayfa Görüntülenme" },
            { key: "analytics", label: "Google Analytics" },
          ] as const).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap
                ${activeTab === key ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* Main Stats */}
          {/* Hero Dashboard Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl border border-amber-200 p-5 flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-200 rounded-xl flex-shrink-0">
                <i className="ri-store-2-line text-amber-700 text-xl"></i>
              </div>
              <div>
                <p className="text-3xl font-bold text-amber-800">{approvedRestaurants}</p>
                <p className="text-xs font-semibold text-amber-600 mt-0.5">Toplam Restoran</p>
                <p className="text-xs text-amber-500">{applications.filter((a) => a.status === "pending").length} başvuru bekliyor</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-stone-50 to-stone-100 rounded-2xl border border-stone-200 p-5 flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-stone-200 rounded-xl flex-shrink-0">
                <i className="ri-hotel-line text-stone-700 text-xl"></i>
              </div>
              <div>
                <p className="text-3xl font-bold text-stone-800">{allAccommodations.length}</p>
                <p className="text-xs font-semibold text-stone-600 mt-0.5">Toplam Konaklama</p>
                <p className="text-xs text-stone-400">{allAccommodations.filter((a) => a.membershipTier === "gold").length} Gold üye</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl border border-emerald-200 p-5 flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-200 rounded-xl flex-shrink-0">
                <i className="ri-newspaper-line text-emerald-700 text-xl"></i>
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-800">{announcementCount}</p>
                <p className="text-xs font-semibold text-emerald-600 mt-0.5">Toplam Duyuru</p>
                <p className="text-xs text-emerald-500">haber, blog, kampanya</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-sky-50 to-sky-100 rounded-2xl border border-sky-200 p-5 flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-sky-200 rounded-xl flex-shrink-0">
                <i className="ri-map-pin-line text-sky-700 text-xl"></i>
              </div>
              <div>
                <p className="text-3xl font-bold text-sky-800">{totalPlaces}</p>
                <p className="text-xs font-semibold text-sky-600 mt-0.5">Toplam Yer</p>
                <p className="text-xs text-sky-500">{totalArticles} makale ile birlikte</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard icon="ri-article-line" label="Toplam Makale" value={totalArticles} sub={`${adminArticles.length} admin ekledi`} color="bg-amber-100 text-amber-600" />
            <StatCard icon="ri-map-pin-line" label="Toplam Yer" value={totalPlaces} sub={`${adminPlaces.length} admin ekledi`} color="bg-emerald-100 text-emerald-600" />
            <StatCard icon="ri-store-2-line" label="Aktif Restoran" value={approvedRestaurants} sub={`${applications.filter((a) => a.status === "pending").length} bekliyor`} color="bg-orange-100 text-orange-600" />
            <StatCard icon="ri-chat-3-line" label="Toplam Yorum" value={totalReviews} sub={`${visibleReviews} görünür`} color="bg-sky-100 text-sky-600" />
            <StatCard icon="ri-star-line" label="Ort. Puan" value={avgRating} sub="tüm yorumlar" color="bg-yellow-100 text-yellow-600" />
            <StatCard icon="ri-eye-off-line" label="Gizli Yorum" value={totalReviews - visibleReviews} sub="moderasyon" color="bg-rose-100 text-rose-600" />
          </div>

          {/* Google Analytics Quick Access */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl border border-orange-200 p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-100 flex-shrink-0">
                  <i className="ri-bar-chart-box-line text-orange-500 text-xl"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-stone-800">Google Analytics</p>
                  <p className="text-xs text-stone-500 mt-0.5">
                    {gaMeasurementId
                      ? <span className="text-emerald-600 font-semibold flex items-center gap-1"><i className="ri-checkbox-circle-fill"></i> Aktif — {gaMeasurementId}</span>
                      : "Gerçek ziyaretçi verilerini görüntüleyin"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  href="https://analytics.google.com/analytics/web/#/realtime"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex items-center gap-1.5 text-xs px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap font-semibold"
                >
                  <i className="ri-user-3-line"></i> Gerçek Zamanlı
                </a>
                <a
                  href="https://analytics.google.com/analytics/web/#/reports"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex items-center gap-1.5 text-xs px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap font-semibold"
                >
                  <i className="ri-bar-chart-2-line"></i> Raporlar
                </a>
                <button
                  onClick={() => setActiveTab("analytics")}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 bg-white border border-orange-200 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap font-semibold"
                >
                  <i className="ri-settings-3-line"></i> GA Ayarları
                </button>
              </div>
            </div>
          </div>

          {/* Quick Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Applications Summary */}
            <div className="bg-white rounded-xl border border-stone-100 p-5">
              <h3 className="text-sm font-bold text-stone-800 mb-4 flex items-center gap-2">
                <i className="ri-store-2-line text-amber-500"></i> Başvuru Özeti
              </h3>
              <div className="space-y-2.5">
                {[
                  { label: "Bekleyen", count: applications.filter((a) => a.status === "pending").length, color: "bg-amber-400" },
                  { label: "Onaylanan", count: applications.filter((a) => a.status === "approved").length, color: "bg-emerald-400" },
                  { label: "Reddedilen", count: applications.filter((a) => a.status === "rejected").length, color: "bg-rose-400" },
                ].map(({ label, count, color }) => (
                  <div key={label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-stone-500">{label}</span>
                      <span className="font-semibold text-stone-700">{count}</span>
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${color} rounded-full transition-all duration-700`}
                        style={{ width: applications.length > 0 ? `${(count / applications.length) * 100}%` : "0%" }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Rating Distribution */}
            <div className="bg-white rounded-xl border border-stone-100 p-5">
              <h3 className="text-sm font-bold text-stone-800 mb-4 flex items-center gap-2">
                <i className="ri-star-line text-amber-500"></i> Puan Dağılımı
              </h3>
              {reviews.length === 0 ? (
                <p className="text-xs text-stone-400 text-center py-4">Henüz yorum yok</p>
              ) : (
                <div className="space-y-2">
                  {ratingDist.map(({ star, count }) => (
                    <div key={star} className="flex items-center gap-2">
                      <span className="text-xs text-stone-500 w-3">{star}</span>
                      <i className="ri-star-fill text-amber-400 text-xs"></i>
                      <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-400 rounded-full transition-all duration-700"
                          style={{ width: `${(count / maxRatingCount) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-stone-400 w-4">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Restaurants */}
            <div className="bg-white rounded-xl border border-stone-100 p-5">
              <h3 className="text-sm font-bold text-stone-800 mb-4 flex items-center gap-2">
                <i className="ri-trophy-line text-amber-500"></i> En Çok Yorum Alan
              </h3>
              {topRestaurants.length === 0 ? (
                <p className="text-xs text-stone-400 text-center py-4">Henüz yorum yok</p>
              ) : (
                <div className="space-y-2.5">
                  {topRestaurants.map(([id, count], i) => (
                    <div key={id} className="flex items-center gap-2">
                      <span className={`text-xs font-bold w-4 ${i === 0 ? "text-amber-500" : i === 1 ? "text-stone-400" : "text-stone-300"}`}>
                        {i + 1}.
                      </span>
                      <span className="text-xs text-stone-600 flex-1 truncate">{id}</span>
                      <span className="text-xs font-semibold text-stone-700 bg-stone-100 px-2 py-0.5 rounded-full">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* REVIEWS TAB */}
      {activeTab === "reviews" && (
        <div className="space-y-5">
          {/* Monthly Chart */}
          <div className="bg-white rounded-xl border border-stone-100 p-5">
            <h3 className="text-sm font-bold text-stone-800 mb-5">Aylık Yorum Sayısı (Son 6 Ay)</h3>
            {reviews.length === 0 ? (
              <p className="text-xs text-stone-400 text-center py-8">Henüz yorum yok</p>
            ) : (
              <div className="flex items-end gap-3 h-36">
                {monthlyReviews.map((m, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                    <div className="w-full flex flex-col justify-end" style={{ height: 100 }}>
                      <div
                        className="w-full bg-amber-400 rounded-t-md transition-all duration-700 group-hover:bg-amber-500"
                        style={{ height: `${(m.count / maxMonthly) * 100}%`, minHeight: m.count > 0 ? 4 : 0 }}
                      ></div>
                    </div>
                    <span className="text-xs text-stone-400">{m.label}</span>
                    {m.count > 0 && <span className="text-xs font-bold text-stone-700">{m.count}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Rating breakdown */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-stone-100 p-5">
              <h3 className="text-sm font-bold text-stone-800 mb-4">Puan Dağılımı</h3>
              <div className="flex items-center gap-4 mb-4">
                <div className="text-center">
                  <p className="text-4xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>{avgRating}</p>
                  <div className="flex items-center gap-0.5 justify-center mt-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i key={s} className={`text-sm ${s <= Math.round(Number(avgRating)) ? "ri-star-fill text-amber-400" : "ri-star-line text-stone-300"}`}></i>
                    ))}
                  </div>
                  <p className="text-xs text-stone-400 mt-1">{totalReviews} yorum</p>
                </div>
                <div className="flex-1 space-y-1.5">
                  {ratingDist.map(({ star, count }) => (
                    <div key={star} className="flex items-center gap-2">
                      <span className="text-xs text-stone-500 w-3">{star}</span>
                      <i className="ri-star-fill text-amber-400 text-xs"></i>
                      <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full" style={{ width: `${(count / Math.max(totalReviews, 1)) * 100}%` }}></div>
                      </div>
                      <span className="text-xs text-stone-400 w-6 text-right">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-stone-100 p-5">
              <h3 className="text-sm font-bold text-stone-800 mb-4">Moderasyon Durumu</h3>
              <div className="space-y-3">
                {[
                  { label: "Görünür Yorum", count: visibleReviews, color: "bg-emerald-400", icon: "ri-eye-line" },
                  { label: "Gizli Yorum", count: totalReviews - visibleReviews, color: "bg-stone-300", icon: "ri-eye-off-line" },
                  { label: "Düşük Puanlı (1-2)", count: reviews.filter((r) => r.rating <= 2).length, color: "bg-rose-400", icon: "ri-alert-line" },
                  { label: "Faydalı Oy Alan", count: reviews.filter((r) => r.helpful > 0).length, color: "bg-amber-400", icon: "ri-thumb-up-line" },
                ].map(({ label, count, color, icon }) => (
                  <div key={label} className="flex items-center gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${color.replace("bg-", "bg-").replace("-400", "-100").replace("-300", "-100")}`}>
                      <i className={`${icon} text-sm ${color.replace("bg-", "text-")}`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-stone-600">{label}</span>
                        <span className="font-semibold text-stone-700">{count}</span>
                      </div>
                      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                        <div className={`h-full ${color} rounded-full`} style={{ width: `${(count / Math.max(totalReviews, 1)) * 100}%` }}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ACCOMMODATIONS TAB */}
      {activeTab === "accommodations" && (() => {
        const goldCount = allAccommodations.filter((a) => a.membershipTier === "gold").length;
        const freeCount = allAccommodations.filter((a) => a.membershipTier !== "gold").length;
        const total = allAccommodations.length;

        // City distribution
        const cityMap: Record<string, { total: number; gold: number }> = {};
        allAccommodations.forEach((a) => {
          if (!cityMap[a.city]) cityMap[a.city] = { total: 0, gold: 0 };
          cityMap[a.city].total += 1;
          if (a.membershipTier === "gold") cityMap[a.city].gold += 1;
        });
        const cityList = Object.entries(cityMap)
          .sort((a, b) => b[1].total - a[1].total)
          .slice(0, 10);
        const maxCity = cityList[0]?.[1].total || 1;

        // Type distribution
        const typeMap: Record<string, number> = {};
        allAccommodations.forEach((a) => { typeMap[a.businessType] = (typeMap[a.businessType] || 0) + 1; });
        const typeList = Object.entries(typeMap).sort((a, b) => b[1] - a[1]);

        // Monthly joins (last 6 months)
        const now = new Date();
        const MONTH_NAMES_TR = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
        const monthlyJoins = Array.from({ length: 6 }, (_, i) => {
          const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
          const label = MONTH_NAMES_TR[d.getMonth()];
          const count = allAccommodations.filter((a) => {
            if (!a.appliedAt) return false;
            const ad = new Date(a.appliedAt);
            return ad.getFullYear() === d.getFullYear() && ad.getMonth() === d.getMonth();
          }).length;
          return { label, count };
        });
        const maxMonthlyJoin = Math.max(...monthlyJoins.map((m) => m.count), 1);

        const typeLabelsMap: Record<string, string> = {
          hotel: "Otel", pansiyon: "Pansiyon", boutique: "Butik Otel",
          apart: "Apart", camping: "Kamp & Glamping", tourism: "Turizm Firması",
        };

        return (
          <div className="space-y-6">
            {/* Top Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-stone-100 p-5 text-center">
                <p className="text-3xl font-bold text-stone-900">{total}</p>
                <p className="text-xs text-stone-500 mt-1">Toplam İşletme</p>
              </div>
              <div className="bg-amber-50 rounded-xl border border-amber-100 p-5 text-center">
                <p className="text-3xl font-bold text-amber-600">{goldCount}</p>
                <p className="text-xs text-stone-500 mt-1 flex items-center justify-center gap-1">
                  <i className="ri-vip-crown-fill text-amber-500 text-xs"></i> Gold Üye
                </p>
              </div>
              <div className="bg-stone-50 rounded-xl border border-stone-100 p-5 text-center">
                <p className="text-3xl font-bold text-stone-600">{freeCount}</p>
                <p className="text-xs text-stone-500 mt-1">Ücretsiz Üye</p>
              </div>
              <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-5 text-center">
                <p className="text-3xl font-bold text-emerald-600">
                  {total > 0 ? Math.round((goldCount / total) * 100) : 0}%
                </p>
                <p className="text-xs text-stone-500 mt-1">Gold Oranı</p>
              </div>
            </div>

            {/* Gold vs Free Donut-style visual */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Membership Distribution */}
              <div className="bg-white rounded-xl border border-stone-100 p-5">
                <h3 className="text-sm font-bold text-stone-800 mb-5 flex items-center gap-2">
                  <i className="ri-pie-chart-line text-amber-500"></i> Üyelik Dağılımı
                </h3>
                <div className="space-y-3">
                  {[
                    { label: "Gold Üye", count: goldCount, color: "bg-amber-400", textColor: "text-amber-700", bg: "bg-amber-50" },
                    { label: "Ücretsiz Üye", count: freeCount, color: "bg-stone-300", textColor: "text-stone-600", bg: "bg-stone-50" },
                  ].map(({ label, count, color, textColor, bg }) => (
                    <div key={label}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${color}`}></div>
                          <span className="text-xs text-stone-600">{label}</span>
                        </div>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${bg} ${textColor}`}>{count}</span>
                      </div>
                      <div className="h-2.5 bg-stone-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${color} rounded-full transition-all duration-700`}
                          style={{ width: total > 0 ? `${(count / total) * 100}%` : "0%" }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Visual ratio bar */}
                <div className="mt-5 pt-4 border-t border-stone-100">
                  <p className="text-xs text-stone-400 mb-2">Gold / Ücretsiz Oranı</p>
                  <div className="flex h-4 rounded-full overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-amber-500 to-yellow-400 transition-all duration-700 flex items-center justify-center"
                      style={{ width: total > 0 ? `${(goldCount / total) * 100}%` : "0%" }}
                    >
                      {goldCount > 0 && <span className="text-white text-xs font-bold">{goldCount}</span>}
                    </div>
                    <div
                      className="bg-stone-200 flex-1 flex items-center justify-center"
                    >
                      {freeCount > 0 && <span className="text-stone-500 text-xs font-bold">{freeCount}</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Type Distribution */}
              <div className="bg-white rounded-xl border border-stone-100 p-5">
                <h3 className="text-sm font-bold text-stone-800 mb-5 flex items-center gap-2">
                  <i className="ri-building-2-line text-amber-500"></i> İşletme Türü Dağılımı
                </h3>
                {typeList.length === 0 ? (
                  <p className="text-xs text-stone-400 text-center py-6">Veri yok</p>
                ) : (
                  <div className="space-y-2.5">
                    {typeList.map(([type, count]) => (
                      <div key={type}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-stone-600">{typeLabelsMap[type] || type}</span>
                          <span className="font-semibold text-stone-700">{count}</span>
                        </div>
                        <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-amber-400 rounded-full transition-all duration-700"
                            style={{ width: `${(count / total) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* City Distribution */}
            <div className="bg-white rounded-xl border border-stone-100 p-5">
              <h3 className="text-sm font-bold text-stone-800 mb-5 flex items-center gap-2">
                <i className="ri-map-pin-line text-amber-500"></i> Şehir Bazlı Dağılım (İlk 10)
              </h3>
              {cityList.length === 0 ? (
                <p className="text-xs text-stone-400 text-center py-6">Veri yok</p>
              ) : (
                <div className="space-y-3">
                  {cityList.map(([city, data]) => (
                    <div key={city} className="flex items-center gap-3">
                      <div className="w-28 flex-shrink-0">
                        <p className="text-xs text-stone-700 font-medium truncate">{city}</p>
                        <p className="text-xs text-stone-400">{data.total} işletme</p>
                      </div>
                      <div className="flex-1 relative h-5 bg-stone-100 rounded-full overflow-hidden">
                        {/* Total bar */}
                        <div
                          className="absolute inset-y-0 left-0 bg-stone-200 rounded-full transition-all duration-700"
                          style={{ width: `${(data.total / maxCity) * 100}%` }}
                        ></div>
                        {/* Gold bar overlay */}
                        {data.gold > 0 && (
                          <div
                            className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full transition-all duration-700"
                            style={{ width: `${(data.gold / maxCity) * 100}%` }}
                          ></div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 w-20 justify-end">
                        <span className="text-xs text-stone-500">{data.total}</span>
                        {data.gold > 0 && (
                          <span className="flex items-center gap-0.5 text-xs text-amber-600 font-semibold bg-amber-50 px-1.5 py-0.5 rounded-full">
                            <i className="ri-vip-crown-fill text-xs"></i>{data.gold}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Legend */}
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-stone-100">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-stone-200"></div>
                  <span className="text-xs text-stone-500">Ücretsiz</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                  <span className="text-xs text-stone-500">Gold</span>
                </div>
              </div>
            </div>

            {/* Monthly Join Chart */}
            <div className="bg-white rounded-xl border border-stone-100 p-5">
              <h3 className="text-sm font-bold text-stone-800 mb-5 flex items-center gap-2">
                <i className="ri-bar-chart-2-line text-amber-500"></i> Aylık Yeni Üye (Son 6 Ay)
              </h3>
              <div className="flex items-end gap-3 h-36">
                {monthlyJoins.map((m, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                    <div className="w-full flex flex-col justify-end" style={{ height: 100 }}>
                      <div
                        className="w-full bg-amber-400 group-hover:bg-amber-500 rounded-t-md transition-all duration-700"
                        style={{ height: `${(m.count / maxMonthlyJoin) * 100}%`, minHeight: m.count > 0 ? 4 : 0 }}
                      ></div>
                    </div>
                    <span className="text-xs text-stone-400">{m.label}</span>
                    {m.count > 0 && <span className="text-xs font-bold text-stone-700">{m.count}</span>}
                  </div>
                ))}
              </div>
              {allAccommodations.filter((a) => a.appliedAt).length === 0 && (
                <p className="text-xs text-stone-400 text-center mt-2">Katılım tarihi verisi henüz yok</p>
              )}
            </div>

            {/* Recent Accommodations */}
            <div className="bg-white rounded-xl border border-stone-100 p-5">
              <h3 className="text-sm font-bold text-stone-800 mb-4 flex items-center gap-2">
                <i className="ri-time-line text-amber-500"></i> Son Eklenen İşletmeler
              </h3>
              <div className="space-y-2">
                {[...allAccommodations]
                  .filter((a) => a.appliedAt)
                  .sort((a, b) => new Date(b.appliedAt!).getTime() - new Date(a.appliedAt!).getTime())
                  .slice(0, 8)
                  .map((acc) => (
                    <div key={acc.id} className="flex items-center gap-3 py-2 border-b border-stone-50 last:border-0">
                      <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${
                        acc.membershipTier === "gold" ? "bg-amber-100" : "bg-stone-100"
                      }`}>
                        <i className={`ri-building-2-line text-xs ${acc.membershipTier === "gold" ? "text-amber-600" : "text-stone-500"}`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-stone-800 truncate">{acc.businessName}</p>
                        <p className="text-xs text-stone-400">{acc.city} · {typeLabelsMap[acc.businessType] || acc.businessType}</p>
                      </div>
                      {acc.membershipTier === "gold" && (
                        <span className="flex items-center gap-0.5 text-xs text-amber-600 font-bold bg-amber-50 px-2 py-0.5 rounded-full flex-shrink-0">
                          <i className="ri-vip-crown-fill text-xs"></i> Gold
                        </span>
                      )}
                      <span className="text-xs text-stone-400 flex-shrink-0">
                        {acc.appliedAt ? new Date(acc.appliedAt).toLocaleDateString("tr-TR") : "—"}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* PAGE VIEWS TAB */}
      {activeTab === "pageviews" && (() => {
        const placeViews = topViewed.filter((p) => p.pageType === "place");
        const restViews = topViewed.filter((p) => p.pageType === "restaurant");
        const totalViews = topViewed.reduce((s, p) => s + p.count, 0);
        const maxCount = topViewed[0]?.count || 1;

        const getPlaceName = (pid: string) => places.find((p) => p.id === pid)?.name || pid;
        const getRestName = (rid: string) => restaurants.find((r) => r.id === rid)?.name || rid;

        return (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-stone-100 p-5 text-center">
                <p className="text-3xl font-bold text-stone-900">{totalViews.toLocaleString("tr-TR")}</p>
                <p className="text-xs text-stone-500 mt-1">Toplam Görüntülenme</p>
              </div>
              <div className="bg-amber-50 rounded-xl border border-amber-100 p-5 text-center">
                <p className="text-3xl font-bold text-amber-600">{placeViews.reduce((s, p) => s + p.count, 0).toLocaleString("tr-TR")}</p>
                <p className="text-xs text-stone-500 mt-1 flex items-center justify-center gap-1"><i className="ri-map-pin-line text-amber-500"></i> Mekan Sayfaları</p>
              </div>
              <div className="bg-rose-50 rounded-xl border border-rose-100 p-5 text-center">
                <p className="text-3xl font-bold text-rose-600">{restViews.reduce((s, p) => s + p.count, 0).toLocaleString("tr-TR")}</p>
                <p className="text-xs text-stone-500 mt-1 flex items-center justify-center gap-1"><i className="ri-restaurant-line text-rose-500"></i> Restoran Sayfaları</p>
              </div>
            </div>

            {topViewed.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-stone-100">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-stone-100 rounded-full">
                  <i className="ri-eye-line text-stone-400 text-2xl"></i>
                </div>
                <p className="text-stone-500 font-medium text-sm">Henüz görüntülenme verisi yok</p>
                <p className="text-stone-400 text-xs mt-1">Mekan ve restoran detay sayfaları ziyaret edildikçe burada görünecek</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Top Places */}
                <div className="bg-white rounded-xl border border-stone-100 p-5">
                  <h3 className="text-sm font-bold text-stone-800 mb-4 flex items-center gap-2">
                    <i className="ri-map-pin-line text-amber-500"></i> En Çok Görüntülenen Mekanlar
                  </h3>
                  {placeViews.length === 0 ? (
                    <p className="text-xs text-stone-400 text-center py-4">Henüz mekan görüntülenmesi yok</p>
                  ) : (
                    <div className="space-y-3">
                      {placeViews.slice(0, 8).map((p, i) => (
                        <div key={p.key} className="flex items-center gap-3">
                          <span className={`text-xs font-bold w-5 flex-shrink-0 ${i === 0 ? "text-amber-500" : i === 1 ? "text-stone-400" : "text-stone-300"}`}>{i + 1}.</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-stone-700 truncate">{getPlaceName(p.pageId)}</p>
                            <div className="h-1.5 bg-stone-100 rounded-full mt-1 overflow-hidden">
                              <div className="h-full bg-amber-400 rounded-full" style={{ width: `${(p.count / maxCount) * 100}%` }}></div>
                            </div>
                          </div>
                          <span className="text-xs font-bold text-stone-600 bg-stone-100 px-2 py-0.5 rounded-full flex-shrink-0">{p.count.toLocaleString("tr-TR")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Top Restaurants */}
                <div className="bg-white rounded-xl border border-stone-100 p-5">
                  <h3 className="text-sm font-bold text-stone-800 mb-4 flex items-center gap-2">
                    <i className="ri-restaurant-line text-rose-500"></i> En Çok Görüntülenen Restoranlar
                  </h3>
                  {restViews.length === 0 ? (
                    <p className="text-xs text-stone-400 text-center py-4">Henüz restoran görüntülenmesi yok</p>
                  ) : (
                    <div className="space-y-3">
                      {restViews.slice(0, 8).map((p, i) => (
                        <div key={p.key} className="flex items-center gap-3">
                          <span className={`text-xs font-bold w-5 flex-shrink-0 ${i === 0 ? "text-rose-500" : i === 1 ? "text-stone-400" : "text-stone-300"}`}>{i + 1}.</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-stone-700 truncate">{getRestName(p.pageId)}</p>
                            <div className="h-1.5 bg-stone-100 rounded-full mt-1 overflow-hidden">
                              <div className="h-full bg-rose-400 rounded-full" style={{ width: `${(p.count / maxCount) * 100}%` }}></div>
                            </div>
                          </div>
                          <span className="text-xs font-bold text-stone-600 bg-stone-100 px-2 py-0.5 rounded-full flex-shrink-0">{p.count.toLocaleString("tr-TR")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Recent visits */}
            {topViewed.length > 0 && (
              <div className="bg-white rounded-xl border border-stone-100 p-5">
                <h3 className="text-sm font-bold text-stone-800 mb-4 flex items-center gap-2">
                  <i className="ri-time-line text-amber-500"></i> Tüm Sayfa Görüntülenmeleri
                </h3>
                <div className="space-y-2">
                  {topViewed.map((p, i) => (
                    <div key={p.key} className="flex items-center gap-3 py-2 border-b border-stone-50 last:border-0">
                      <span className="text-xs text-stone-400 w-5 flex-shrink-0">{i + 1}.</span>
                      <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${p.pageType === "place" ? "bg-amber-100" : "bg-rose-100"}`}>
                        <i className={`text-xs ${p.pageType === "place" ? "ri-map-pin-line text-amber-600" : "ri-restaurant-line text-rose-600"}`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-stone-800 truncate">
                          {p.pageType === "place" ? getPlaceName(p.pageId) : getRestName(p.pageId)}
                        </p>
                        <p className="text-xs text-stone-400">{p.pageType === "place" ? "Mekan" : "Restoran"} · Son: {new Date(p.lastVisited).toLocaleDateString("tr-TR")}</p>
                      </div>
                      <span className="text-xs font-bold text-stone-700 bg-stone-100 px-2 py-0.5 rounded-full flex-shrink-0">{p.count.toLocaleString("tr-TR")} görüntülenme</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* GOOGLE ANALYTICS TAB */}
      {activeTab === "analytics" && (
        <div className="space-y-6">

          {/* GA Measurement ID Setup */}
          <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
            <div className="px-6 py-5 border-b border-stone-100 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-100 flex-shrink-0">
                <i className="ri-bar-chart-box-line text-orange-500 text-xl"></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-stone-900">Google Analytics Bağlantısı</h3>
                <p className="text-xs text-stone-400 mt-0.5">Measurement ID girerek GA paneline hızlı erişim sağlayın</p>
              </div>
              {gaMeasurementId && (
                <span className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">
                  <i className="ri-checkbox-circle-fill text-emerald-500"></i> Bağlı: {gaMeasurementId}
                </span>
              )}
            </div>
            <div className="p-6">
              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-64">
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                    Google Analytics Measurement ID
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                      <i className="ri-key-2-line text-stone-400 text-sm"></i>
                    </div>
                    <input
                      type="text"
                      value={gaIdInput}
                      onChange={(e) => { setGaIdInput(e.target.value); setGaIdSaved(false); }}
                      placeholder="G-XXXXXXXXXX"
                      className="w-full border border-stone-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-stone-800 outline-none focus:border-orange-400 transition-colors font-mono"
                    />
                  </div>
                  <p className="text-xs text-stone-400 mt-1">
                    Google Analytics &gt; Yönetici &gt; Veri Akışları &gt; Measurement ID (G-... ile başlar)
                  </p>
                </div>
                <button
                  onClick={() => {
                    const trimmed = gaIdInput.trim();
                    if (!trimmed) return;
                    localStorage.setItem("gaMeasurementId", trimmed);
                    setGaMeasurementId(trimmed);
                    setGaIdSaved(true);
                    setTimeout(() => setGaIdSaved(false), 3000);
                  }}
                  className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  {gaIdSaved
                    ? <><i className="ri-checkbox-circle-line"></i> Kaydedildi!</>
                    : <><i className="ri-save-line"></i> Kaydet</>}
                </button>
              </div>
            </div>
          </div>

          {/* Direct GA Panel Links */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6">
            <h3 className="text-sm font-bold text-stone-800 mb-5 flex items-center gap-2">
              <i className="ri-external-link-line text-orange-500"></i> Google Analytics Paneline Hızlı Erişim
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                {
                  icon: "ri-home-4-line",
                  label: "Ana Panel",
                  desc: "Genel bakış ve özet istatistikler",
                  url: "https://analytics.google.com/analytics/web/",
                  color: "bg-orange-50 border-orange-200 hover:bg-orange-100",
                  iconColor: "text-orange-500 bg-orange-100",
                },
                {
                  icon: "ri-user-3-line",
                  label: "Gerçek Zamanlı",
                  desc: "Şu an sitede kaç kişi var",
                  url: "https://analytics.google.com/analytics/web/#/realtime",
                  color: "bg-emerald-50 border-emerald-200 hover:bg-emerald-100",
                  iconColor: "text-emerald-500 bg-emerald-100",
                },
                {
                  icon: "ri-bar-chart-2-line",
                  label: "Raporlar",
                  desc: "Ziyaretçi, sayfa, trafik raporları",
                  url: "https://analytics.google.com/analytics/web/#/reports",
                  color: "bg-amber-50 border-amber-200 hover:bg-amber-100",
                  iconColor: "text-amber-500 bg-amber-100",
                },
                {
                  icon: "ri-map-pin-line",
                  label: "Coğrafi Rapor",
                  desc: "Hangi şehir/ülkeden ziyaret geliyor",
                  url: "https://analytics.google.com/analytics/web/#/reports/explorer?params=_u..nav%3Dmaui%26_r.explorerCard..selmet%3D%5B%22activeUsers%22%5D%26_r.explorerCard..seldim%3D%5B%22city%22%5D",
                  color: "bg-stone-50 border-stone-200 hover:bg-stone-100",
                  iconColor: "text-stone-500 bg-stone-100",
                },
                {
                  icon: "ri-smartphone-line",
                  label: "Cihaz Raporu",
                  desc: "Mobil, masaüstü, tablet dağılımı",
                  url: "https://analytics.google.com/analytics/web/#/reports/explorer?params=_u..nav%3Dmaui%26_r.explorerCard..selmet%3D%5B%22activeUsers%22%5D%26_r.explorerCard..seldim%3D%5B%22deviceCategory%22%5D",
                  color: "bg-stone-50 border-stone-200 hover:bg-stone-100",
                  iconColor: "text-stone-500 bg-stone-100",
                },
                {
                  icon: "ri-share-line",
                  label: "Trafik Kaynakları",
                  desc: "Organik, sosyal, direkt trafik",
                  url: "https://analytics.google.com/analytics/web/#/reports/explorer?params=_u..nav%3Dmaui%26_r.explorerCard..selmet%3D%5B%22activeUsers%22%5D%26_r.explorerCard..seldim%3D%5B%22sessionDefaultChannelGrouping%22%5D",
                  color: "bg-stone-50 border-stone-200 hover:bg-stone-100",
                  iconColor: "text-stone-500 bg-stone-100",
                },
                {
                  icon: "ri-file-list-3-line",
                  label: "En Çok Görüntülenen Sayfalar",
                  desc: "Hangi sayfalar en çok ziyaret ediliyor",
                  url: "https://analytics.google.com/analytics/web/#/reports/explorer?params=_u..nav%3Dmaui%26_r.explorerCard..selmet%3D%5B%22screenPageViews%22%5D%26_r.explorerCard..seldim%3D%5B%22pagePath%22%5D",
                  color: "bg-stone-50 border-stone-200 hover:bg-stone-100",
                  iconColor: "text-stone-500 bg-stone-100",
                },
                {
                  icon: "ri-search-line",
                  label: "Arama Konsolu",
                  desc: "Google Search Console entegrasyonu",
                  url: "https://search.google.com/search-console",
                  color: "bg-stone-50 border-stone-200 hover:bg-stone-100",
                  iconColor: "text-stone-500 bg-stone-100",
                },
                {
                  icon: "ri-settings-3-line",
                  label: "GA Yönetici",
                  desc: "Hesap ve mülk ayarları",
                  url: "https://analytics.google.com/analytics/web/#/admin",
                  color: "bg-stone-50 border-stone-200 hover:bg-stone-100",
                  iconColor: "text-stone-500 bg-stone-100",
                },
              ].map((item) => (
                <a
                  key={item.label}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className={`flex items-center gap-3 p-4 rounded-xl border transition-all cursor-pointer ${item.color}`}
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${item.iconColor}`}>
                    <i className={`${item.icon} text-base`}></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-stone-800">{item.label}</p>
                    <p className="text-xs text-stone-500 mt-0.5 leading-tight">{item.desc}</p>
                  </div>
                  <div className="ml-auto w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-external-link-line text-stone-400 text-xs"></i>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* GA Setup Guide */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6">
            <h3 className="text-sm font-bold text-stone-800 mb-5 flex items-center gap-2">
              <i className="ri-question-line text-orange-500"></i> Google Analytics Nasıl Kurulur?
            </h3>
            <div className="space-y-4">
              {[
                {
                  step: "1",
                  title: "Google Analytics Hesabı Oluştur",
                  desc: "analytics.google.com adresine git, Google hesabınla giriş yap ve yeni bir GA4 mülkü oluştur.",
                  link: "https://analytics.google.com",
                  linkLabel: "analytics.google.com",
                },
                {
                  step: "2",
                  title: "Web Veri Akışı Ekle",
                  desc: "Yönetici &gt; Veri Akışları &gt; Akış Ekle &gt; Web seç. Site URL'ini gir ve akışı oluştur.",
                  link: null,
                  linkLabel: null,
                },
                {
                  step: "3",
                  title: "Measurement ID'yi Kopyala",
                  desc: "Oluşturulan veri akışında G-XXXXXXXXXX formatındaki Measurement ID'yi kopyala.",
                  link: null,
                  linkLabel: null,
                },
                {
                  step: "4",
                  title: "Yukarıya Yapıştır",
                  desc: "Bu sayfanın üstündeki alana Measurement ID'yi yapıştır ve Kaydet'e tıkla. Kod zaten sitenize eklenmiş durumda.",
                  link: null,
                  linkLabel: null,
                },
              ].map((item) => (
                <div key={item.step} className="flex items-start gap-4">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-orange-100 text-orange-600 text-xs font-bold flex-shrink-0 mt-0.5">
                    {item.step}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-stone-800">{item.title}</p>
                    <p className="text-xs text-stone-500 mt-0.5 leading-relaxed">{item.desc}</p>
                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="text-xs text-orange-500 hover:text-orange-600 font-medium mt-1 inline-flex items-center gap-1"
                      >
                        <i className="ri-external-link-line text-xs"></i> {item.linkLabel}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Status Banner */}
          <div className={`rounded-xl border p-4 flex items-start gap-3 ${gaMeasurementId ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className={`text-base ${gaMeasurementId ? "ri-checkbox-circle-fill text-emerald-500" : "ri-error-warning-line text-amber-500"}`}></i>
            </div>
            <div>
              {gaMeasurementId ? (
                <>
                  <p className="text-xs font-semibold text-emerald-800">Google Analytics Aktif — {gaMeasurementId}</p>
                  <p className="text-xs text-emerald-700 mt-0.5 leading-relaxed">
                    Gerçek zamanlı veriler için{" "}
                    <a
                      href="https://analytics.google.com"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="underline font-semibold hover:text-emerald-900"
                    >
                      Google Analytics panelini
                    </a>{" "}
                    ziyaret edin. Veriler genellikle 24-48 saat içinde görünmeye başlar.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs font-semibold text-amber-800">Google Analytics Measurement ID Girilmemiş</p>
                  <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                    Yukarıdaki alana G-XXXXXXXXXX formatındaki ID&apos;yi girerek GA bağlantısını tamamlayın.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CONTENT TAB */}
      {activeTab === "content" && (
        <div className="space-y-5">
          {/* Content Overview */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl border border-stone-100 p-4 text-center">
              <p className="text-3xl font-bold text-amber-500">{totalArticles}</p>
              <p className="text-xs text-stone-500 mt-1">Toplam Makale</p>
              <p className="text-xs text-stone-300 mt-0.5">{BUILTIN_ARTICLES} yerleşik + {adminArticles.length} admin</p>
            </div>
            <div className="bg-white rounded-xl border border-stone-100 p-4 text-center">
              <p className="text-3xl font-bold text-emerald-500">{totalPlaces}</p>
              <p className="text-xs text-stone-500 mt-1">Toplam Yer</p>
              <p className="text-xs text-stone-300 mt-0.5">{BUILTIN_PLACES} yerleşik + {adminPlaces.length} admin</p>
            </div>
            <div className="bg-white rounded-xl border border-stone-100 p-4 text-center">
              <p className="text-3xl font-bold text-orange-500">{approvedRestaurants}</p>
              <p className="text-xs text-stone-500 mt-1">Aktif Restoran</p>
              <p className="text-xs text-stone-300 mt-0.5">{BUILTIN_RESTAURANTS} yerleşik + onaylananlar</p>
            </div>
            <div className="bg-white rounded-xl border border-stone-100 p-4 text-center">
              <p className="text-3xl font-bold text-stone-700">{adminArticles.filter((a) => a.featured).length}</p>
              <p className="text-xs text-stone-500 mt-1">Öne Çıkan Makale</p>
              <p className="text-xs text-stone-300 mt-0.5">admin tarafından</p>
            </div>
          </div>

          {/* Admin Articles by Category */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-stone-100 p-5">
              <h3 className="text-sm font-bold text-stone-800 mb-4">Admin Makaleleri — Kategori Dağılımı</h3>
              {categoryList.length === 0 ? (
                <p className="text-xs text-stone-400 text-center py-6">Henüz admin makalesi yok</p>
              ) : (
                <div className="space-y-2.5">
                  {categoryList.map(([cat, count]) => (
                    <div key={cat}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-stone-600">{cat}</span>
                        <span className="font-semibold text-stone-700">{count}</span>
                      </div>
                      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-400 rounded-full"
                          style={{ width: `${(count / adminArticles.length) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Admin Places by City */}
            <div className="bg-white rounded-xl border border-stone-100 p-5">
              <h3 className="text-sm font-bold text-stone-800 mb-4">Admin Yerleri — Şehir Dağılımı</h3>
              {adminPlaces.length === 0 ? (
                <p className="text-xs text-stone-400 text-center py-6">Henüz admin yeri yok</p>
              ) : (
                (() => {
                  const cityMap: Record<string, number> = {};
                  adminPlaces.forEach((p) => { cityMap[p.city] = (cityMap[p.city] || 0) + 1; });
                  const cityList = Object.entries(cityMap).sort((a, b) => b[1] - a[1]).slice(0, 6);
                  const maxC = cityList[0]?.[1] || 1;
                  return (
                    <div className="space-y-2.5">
                      {cityList.map(([city, count]) => (
                        <div key={city}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-stone-600">{city}</span>
                            <span className="font-semibold text-stone-700">{count}</span>
                          </div>
                          <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${(count / maxC) * 100}%` }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()
              )}
            </div>
          </div>

          {/* Recent Admin Content */}
          {(adminArticles.length > 0 || adminPlaces.length > 0) && (
            <div className="bg-white rounded-xl border border-stone-100 p-5">
              <h3 className="text-sm font-bold text-stone-800 mb-4">Son Eklenen İçerikler</h3>
              <div className="space-y-2">
                {[
                  ...adminArticles.map((a) => ({ type: "Makale", name: a.title, date: a.createdAt, icon: "ri-article-line", color: "text-amber-500 bg-amber-50" })),
                  ...adminPlaces.map((p) => ({ type: "Yer", name: p.name, date: p.createdAt, icon: "ri-map-pin-line", color: "text-emerald-500 bg-emerald-50" })),
                ]
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .slice(0, 8)
                  .map((item, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-stone-50 last:border-0">
                      <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${item.color}`}>
                        <i className={`${item.icon} text-xs`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-stone-800 truncate">{item.name}</p>
                        <p className="text-xs text-stone-400">{item.type}</p>
                      </div>
                      <span className="text-xs text-stone-400 flex-shrink-0">
                        {new Date(item.date).toLocaleDateString("tr-TR")}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SiteStats;
